import { useEffect, useRef } from 'react';
import type { QueueRealtimeEvent } from '../queue/types';
import { subscribeToQueueRealtime, type QueueRealtimeSubscription } from '../queueRealtimeClient';

type UseQueueRealtimeOptions = {
  sessionId: string | null | undefined;
  userId: string | null | undefined;
  onEvent: (event: QueueRealtimeEvent) => void;
  onReconnect?: () => void;
  onError?: (error: unknown) => void;
  enabled?: boolean;
};

const NOOP = () => {};

export const useQueueRealtimeSubscription = ({
  sessionId,
  userId,
  onEvent,
  onReconnect,
  onError,
  enabled = true
}: UseQueueRealtimeOptions): void => {
  const eventHandlerRef = useRef(onEvent);
  const reconnectHandlerRef = useRef(onReconnect ?? NOOP);
  const errorHandlerRef = useRef(onError ?? NOOP);

  useEffect(() => {
    eventHandlerRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    reconnectHandlerRef.current = onReconnect ?? NOOP;
  }, [onReconnect]);

  useEffect(() => {
    errorHandlerRef.current = onError ?? NOOP;
  }, [onError]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!sessionId || !userId) {
      return;
    }

    let subscription: QueueRealtimeSubscription | null = null;
    const hasConnectedRef = { current: false };

    subscription = subscribeToQueueRealtime({
      sessionId,
      userId,
      onEvent: (event) => {
        eventHandlerRef.current?.(event);
      },
      onError: (error) => {
        errorHandlerRef.current?.(error);
      },
      onOpen: () => {
        if (hasConnectedRef.current) {
          reconnectHandlerRef.current?.();
        } else {
          hasConnectedRef.current = true;
        }
      },
      onReconnectScheduled: () => {
        hasConnectedRef.current = true;
      }
    });

    return () => {
      subscription?.close();
      subscription = null;
    };
  }, [enabled, sessionId, userId]);
};
